import type { ProductCategoryType, ProductType } from "@/api/product/types/product"

export const DEFAULT_PRODUCT_PRIMARY_CATEGORY_ID = -1
export const DEFAULT_PRODUCT_PRIMARY_CATEGORY_NAME = "默认分类"

type SortableProduct = {
  sort?: number | null
}

type ProductIdValue = number | string | null | undefined
type ProductWithId = SortableProduct & {
  itemId?: ProductIdValue
}
type ProductWithSales = ProductWithId & {
  isSales?: number | null
}
type ProductFieldPatch<TProduct extends ProductWithId> = Partial<TProduct> | ((product: TProduct) => Partial<TProduct>)

type ProductCategoryTreeBase = Omit<ProductCategoryType, "children" | "productList">

export interface ProductCategoryTreeNode<TProduct extends SortableProduct = ProductType> extends ProductCategoryTreeBase {
  categoryId?: number | null
  categoryName?: string | null
  categoryLevel?: number | null
  parentCategoryId?: number | null
  parentCategoryName?: string | null
  children?: ProductCategoryTreeNode<TProduct>[] | null
  productList?: TProduct[] | null
}

export interface ProductSecondCategoryNode<TProduct extends SortableProduct = ProductType> extends ProductCategoryTreeNode<TProduct> {
  categoryId: number
  categoryName: string
  categoryLevel: 2
  parentCategoryId: number
  parentCategoryName: string
  productList: TProduct[]
}

export interface ProductPrimaryCategoryNode<TProduct extends SortableProduct = ProductType> extends ProductCategoryTreeNode<TProduct> {
  categoryId: number
  categoryName: string
  categoryLevel: 1
  children: ProductSecondCategoryNode<TProduct>[]
}

export interface ProductCategoryProductGroup<TProduct extends SortableProduct = ProductType> extends ProductSecondCategoryNode<TProduct> {
  secondCategoryId: number
  secondCategoryName: string
  products: TProduct[]
}

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === "number" && Number.isFinite(value)
}

export const normalizeProductCategoryParentIdForRequest = (parentCategoryId?: number | null) => {
  if (!isFiniteNumber(parentCategoryId) || parentCategoryId <= 0) {
    return null
  }
  return parentCategoryId
}

const getCategoryId = <TProduct extends SortableProduct>(category: ProductCategoryTreeNode<TProduct>, fallback: number) => {
  return isFiniteNumber(category.categoryId) ? category.categoryId : fallback
}

const getCategoryName = <TProduct extends SortableProduct>(category: ProductCategoryTreeNode<TProduct>, fallback: string) => {
  return category.categoryName || fallback
}

const getPrimaryCategoryId = <TProduct extends SortableProduct>(
  category: ProductCategoryTreeNode<TProduct>,
  fallback: number,
) => {
  if (isFiniteNumber(category.categoryId)) {
    return category.categoryId
  }
  if (category.categoryName === DEFAULT_PRODUCT_PRIMARY_CATEGORY_NAME || category.categoryLevel === 1) {
    return DEFAULT_PRODUCT_PRIMARY_CATEGORY_ID
  }
  return fallback
}

const bySort = <TProduct extends SortableProduct>(a: ProductCategoryTreeNode<TProduct>, b: ProductCategoryTreeNode<TProduct>) => {
  return (a.sort ?? 0) - (b.sort ?? 0)
}

const byProductSort = <TProduct extends SortableProduct>(a: TProduct, b: TProduct) => {
  return (a.sort ?? 0) - (b.sort ?? 0)
}

const getProductItemKey = (product: ProductWithId) => {
  return product.itemId == null ? "" : String(product.itemId)
}

const normalizeProductItemIdSet = (itemIds: ProductIdValue[] | ProductIdValue) => {
  const rawIds = Array.isArray(itemIds) ? itemIds : [itemIds]
  return new Set(
    rawIds
      .filter(itemId => itemId != null && itemId !== "")
      .map(itemId => String(itemId)),
  )
}

const createDefaultPrimary = <TProduct extends SortableProduct = ProductType>(): ProductPrimaryCategoryNode<TProduct> => ({
  categoryId: DEFAULT_PRODUCT_PRIMARY_CATEGORY_ID,
  categoryName: DEFAULT_PRODUCT_PRIMARY_CATEGORY_NAME,
  categoryLevel: 1,
  parentCategoryId: null,
  children: [],
  isShopDisplay: 1,
  sort: Number.MAX_SAFE_INTEGER,
})

const toSecondCategory = <TProduct extends SortableProduct>(
  category: ProductCategoryTreeNode<TProduct>,
  parent: ProductPrimaryCategoryNode<TProduct>,
): ProductSecondCategoryNode<TProduct> | null => {
  const categoryId = getCategoryId(category, 0)
  if (!categoryId) {
    return null
  }

  const productList = (category.productList ?? []).slice().sort(byProductSort)
  return {
    ...category,
    categoryId,
    categoryName: getCategoryName(category, "未命名分类"),
    categoryLevel: 2,
    parentCategoryId: parent.categoryId,
    parentCategoryName: parent.categoryName,
    productList,
    children: [],
  }
}

export const normalizeProductCategoryTree = <TProduct extends SortableProduct = ProductType>(
  source: ProductCategoryTreeNode<TProduct>[] = [],
): ProductPrimaryCategoryNode<TProduct>[] => {
  const primaryList: ProductPrimaryCategoryNode<TProduct>[] = []
  const primaryMap = new Map<number, ProductPrimaryCategoryNode<TProduct>>()
  let defaultPrimary: ProductPrimaryCategoryNode<TProduct> | null = null

  const ensureDefaultPrimary = () => {
    if (!defaultPrimary) {
      defaultPrimary = createDefaultPrimary<TProduct>()
      primaryList.push(defaultPrimary)
      primaryMap.set(defaultPrimary.categoryId, defaultPrimary)
    }
    return defaultPrimary
  }

  source.slice().sort(bySort).forEach((category, index) => {
    if (category.categoryLevel === 1 || (category.categoryLevel == null && Array.isArray(category.children))) {
      const categoryId = getPrimaryCategoryId(category, index + 1)
      const primary: ProductPrimaryCategoryNode<TProduct> = {
        ...category,
        categoryId,
        categoryName: getCategoryName(category, "未命名分类"),
        categoryLevel: 1,
        children: [],
      }
      primaryList.push(primary)
      primaryMap.set(categoryId, primary)

      ;(category.children ?? []).slice().sort(bySort).forEach((child) => {
        const secondCategory = toSecondCategory(child, primary)
        if (secondCategory) {
          primary.children.push(secondCategory)
        }
      })
      return
    }

    const parent = isFiniteNumber(category.parentCategoryId)
      ? primaryMap.get(category.parentCategoryId) ?? ensureDefaultPrimary()
      : ensureDefaultPrimary()
    const secondCategory = toSecondCategory(category, parent)
    if (secondCategory) {
      parent.children.push(secondCategory)
    }
  })

  return primaryList
    .map(primary => ({
      ...primary,
      children: primary.children.slice().sort(bySort),
    }))
    .filter(primary => primary.categoryId !== DEFAULT_PRODUCT_PRIMARY_CATEGORY_ID || primary.children.length > 0)
    .sort(bySort)
}

export const flattenProductSecondCategories = <TProduct extends SortableProduct = ProductType>(
  primaryList: ProductPrimaryCategoryNode<TProduct>[],
) => {
  return primaryList.flatMap(primary => primary.children)
}

export const buildProductCategoryGroups = <TProduct extends SortableProduct = ProductType>(
  secondCategories: ProductSecondCategoryNode<TProduct>[],
): ProductCategoryProductGroup<TProduct>[] => {
  return secondCategories.map(secondCategory => ({
    ...secondCategory,
    secondCategoryId: secondCategory.categoryId,
    secondCategoryName: secondCategory.categoryName,
    products: (secondCategory.productList ?? []).slice(),
  }))
}

export const buildProductCategoryGroupsFromPrimaryList = <TProduct extends SortableProduct = ProductType>(
  primaryList: ProductPrimaryCategoryNode<TProduct>[],
) => {
  return buildProductCategoryGroups(flattenProductSecondCategories(primaryList))
}

const transformProductListByIds = <TProduct extends ProductWithId>(
  productList: TProduct[] = [],
  itemIdSet: Set<string>,
  transformProduct: (product: TProduct) => TProduct | null,
) => {
  if (itemIdSet.size === 0) {
    return productList
  }

  let changed = false
  const nextProductList: TProduct[] = []

  productList.forEach((product) => {
    if (!itemIdSet.has(getProductItemKey(product))) {
      nextProductList.push(product)
      return
    }

    const nextProduct = transformProduct(product)
    if (nextProduct === product) {
      nextProductList.push(product)
      return
    }

    changed = true
    if (nextProduct) {
      nextProductList.push(nextProduct)
    }
  })

  return changed ? nextProductList : productList
}

const transformProductsInCategoryTree = <TProduct extends ProductWithId>(
  primaryList: ProductPrimaryCategoryNode<TProduct>[],
  itemIds: ProductIdValue[] | ProductIdValue,
  transformProduct: (product: TProduct) => TProduct | null,
) => {
  const itemIdSet = normalizeProductItemIdSet(itemIds)
  if (itemIdSet.size === 0) {
    return primaryList
  }

  let treeChanged = false
  const nextPrimaryList = primaryList.map((primaryCategory) => {
    let primaryChanged = false
    const nextChildren = primaryCategory.children.map((secondCategory) => {
      const nextProductList = transformProductListByIds(
        secondCategory.productList,
        itemIdSet,
        transformProduct,
      )

      if (nextProductList === secondCategory.productList) {
        return secondCategory
      }

      primaryChanged = true
      return {
        ...secondCategory,
        productList: nextProductList,
      }
    })

    if (!primaryChanged) {
      return primaryCategory
    }

    treeChanged = true
    return {
      ...primaryCategory,
      children: nextChildren,
    }
  })

  return treeChanged ? nextPrimaryList : primaryList
}

export const patchProductsInCategoryTree = <TProduct extends ProductWithId>(
  primaryList: ProductPrimaryCategoryNode<TProduct>[],
  itemIds: ProductIdValue[] | ProductIdValue,
  patch: ProductFieldPatch<TProduct>,
) => {
  return transformProductsInCategoryTree(primaryList, itemIds, (product) => {
    const nextPatch = typeof patch === "function" ? patch(product) : patch
    const changed = (Object.keys(nextPatch) as (keyof TProduct)[]).some((key) => {
      return !Object.is(product[key], nextPatch[key])
    })

    if (!changed) {
      return product
    }

    return {
      ...product,
      ...nextPatch,
    }
  })
}

export const patchProductSalesInCategoryTree = <TProduct extends ProductWithSales>(
  primaryList: ProductPrimaryCategoryNode<TProduct>[],
  itemIds: ProductIdValue[] | ProductIdValue,
  isSales: number | boolean,
) => {
  const nextIsSales = Number(isSales) === 1 ? 1 : 0
  return patchProductsInCategoryTree(primaryList, itemIds, {
    isSales: nextIsSales,
  } as Partial<TProduct>)
}

export const removeProductsFromCategoryTree = <TProduct extends ProductWithId>(
  primaryList: ProductPrimaryCategoryNode<TProduct>[],
  itemIds: ProductIdValue[] | ProductIdValue,
) => {
  return transformProductsInCategoryTree(primaryList, itemIds, () => null)
}
